// Доступ к 1С через кеширующий прокси ядра (BFF: /api/1c/<база>/<путь OData>).
//
// Почему это отдельный модуль, а не часть client.ts: у 1С другой контракт. Ответ приходит
// «тело 1С как есть» плюс служебные поля (_cache, _age_sec, _ttl_sec, _stale), а отказ —
// это 503 с машинным кодом причины. Обычный request() из client.ts превратил бы такой отказ
// в безликую ApiError, и экран не смог бы отличить «1С молчит» от «нет такой сущности».
//
// ТОЛЬКО ЧТЕНИЕ. Записи у прокси нет вовсе — здесь нет и функции, которой её можно позвать.
import { useQuery } from '@tanstack/react-query'

import { Unauthorized } from './client'

/** Базы 1С, которые выпускает ядро: ut — склад (УТ), bp — бухгалтерия. */
export type OdataBase = 'ut' | 'bp'

/**
 * Причина отказа. Первые четыре приходят от ядра (см. OdataDown в core_api.py),
 * core_unreachable добавляет BFF, когда до самого ядра не достучаться.
 */
export type OdataCode =
  | 'timeout' | 'upstream' | 'busy' | 'not_configured'
  | 'bad_request' | 'forbidden_entity' | 'no_cache' | 'internal'
  | 'core_unreachable' | 'unknown'

/** Свежесть ответа. Ядро отдаёт её при каждом ответе — 1С меняется в течение дня. */
export interface OdataMeta {
  /** hit — свежий кеш, miss — только что из 1С, stale — 1С не ответила, отдан старый кеш. */
  cache: 'hit' | 'miss' | 'stale'
  /** Возраст данных в секундах. У miss — 0. */
  ageSec: number
  /** Сколько ядро считает такой ответ свежим (для остатков — 180 с). */
  ttlSec: number
  /** true — данным нельзя доверять: это старый кеш, потому что 1С недоступна. */
  stale: boolean
  /** Что именно ответила 1С, когда пришлось отдать старый кеш. */
  error?: string
}

export interface OdataResult<T> {
  /** Массив записей: 1С кладёт их в value. */
  rows: T[]
  meta: OdataMeta
}

/**
 * 1С недоступна или запрос ей не подходит. Отдельный тип, а не ApiError: экран обязан
 * показать причину человеку. Пустая таблица здесь читалась бы как «на складе ничего нет»,
 * а это неправда — мы просто не знаем остатков.
 */
export class OdataDown extends Error {
  code: OdataCode
  status: number
  constructor(code: OdataCode, message: string, status: number) {
    super(message)
    this.name = 'OdataDown'
    this.code = code
    this.status = status
  }
}

/** Человеческий текст отказа: код 1С сам по себе пользователю ничего не говорит. */
export function odataMessage(err: unknown): string {
  if (!(err instanceof OdataDown)) return err instanceof Error ? err.message : String(err)
  switch (err.code) {
    case 'timeout':
      return '1С не ответила вовремя. Обычно это перегруженный сервер 1С — попробуйте через минуту.'
    case 'busy':
      return 'В 1С уже очередь запросов — ядро не стало добавлять свой. Подождите несколько секунд и повторите.'
    case 'upstream':
      return `1С ответила ошибкой: ${err.message}`
    case 'not_configured':
      return 'Доступ к базе 1С в ядре не настроен (ODATA_*_BASE / ODATA_*_AUTH в .env). Это настройка сервера, не ваша ошибка.'
    case 'forbidden_entity':
      return `Ядро не выпускает эту сущность 1С: ${err.message}`
    case 'bad_request':
      return `Ядро не приняло запрос к 1С: ${err.message}`
    case 'core_unreachable':
      return 'Ядро (cir-core-api) недоступно — до 1С запрос даже не дошёл.'
    default:
      return err.message || 'Данные из 1С получить не удалось.'
  }
}

/** «2 мин» / «14 с» — возраст данных словами. Часы показываем как часы: 5400 с нечитаемы. */
export function fmtAge(sec: number): string {
  const s = Math.max(0, Math.round(sec))
  if (s < 90) return `${s} с`
  const m = Math.round(s / 60)
  if (m < 90) return `${m} мин`
  return `${Math.round(m / 60)} ч`
}

function parseMeta(body: Record<string, unknown>): OdataMeta {
  const cache = body._cache
  return {
    cache: cache === 'hit' || cache === 'stale' ? cache : 'miss',
    ageSec: Number(body._age_sec) || 0,
    ttlSec: Number(body._ttl_sec) || 0,
    stale: !!body._stale,
    error: typeof body._1c_error === 'string' ? body._1c_error : undefined,
  }
}

/**
 * Свежесть пачки ответов одним значением. Экран склада собирает позицию из нескольких
 * запросов (номенклатура + склады + остатки), и честно показать надо САМОЕ СТАРОЕ из них:
 * «свежо» рядом с остатком, взятым из вчерашнего кеша, — это враньё.
 */
export function mergeMeta(parts: Array<OdataMeta | undefined>): OdataMeta | undefined {
  const list = parts.filter((m): m is OdataMeta => !!m)
  if (!list.length) return undefined
  let worst = list[0]
  for (const m of list) {
    // stale важнее возраста: устаревший ответ обесценивает всю картинку
    if ((m.stale && !worst.stale) || (m.stale === worst.stale && m.ageSec > worst.ageSec)) worst = m
  }
  // cache показываем как «худший» режим: если хоть что-то ходило в 1С, это уже не чистый hit
  const cache: OdataMeta['cache'] = list.some((m) => m.stale)
    ? 'stale'
    : list.some((m) => m.cache === 'miss') ? 'miss' : 'hit'
  return { ...worst, cache }
}

/**
 * Один запрос к 1С. `path` — путь OData как есть, вместе со строкой запроса
 * (`Catalog_Номенклатура?$filter=…`). Кодировать значения фильтра должен вызывающий:
 * ядро путь НЕ перекодирует, иначе ломается кириллица в $filter.
 *
 * `maxAge` — «мне хватит данных не старше N секунд». Ядро всё равно не пустит запрос
 * мимо кеша (минимум 30 с), это ограничение сверху, а не способ его обойти.
 */
export async function odataGet<T>(
  base: OdataBase, path: string, maxAge?: number,
): Promise<OdataResult<T>> {
  const sep = path.includes('?') ? '&' : '?'
  const url = `/api/1c/${base}/${path}${maxAge ? `${sep}max_age=${Math.round(maxAge)}` : ''}`
  const res = await fetch(url, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()

  const text = await res.text()
  let body: Record<string, unknown>
  try {
    body = text ? (JSON.parse(text) as Record<string, unknown>) : {}
  } catch {
    // Ядро всегда отвечает JSON; не-JSON здесь — это чужая страница-заглушка перед ним.
    throw new OdataDown('upstream', `ответ не разобрался как JSON: ${text.slice(0, 160)}`, res.status)
  }

  if (!res.ok) {
    const code = typeof body.code === 'string' ? (body.code as OdataCode) : 'unknown'
    const msg = typeof body.error === 'string' ? body.error : `HTTP ${res.status}`
    throw new OdataDown(code, msg, res.status)
  }

  const value = body.value
  return { rows: Array.isArray(value) ? (value as T[]) : [], meta: parseMeta(body) }
}

/**
 * Хук на один запрос к 1С. Повторять отказ смысла мало: 1С либо не отвечает, либо
 * сущность не разрешена — и то и другое от повтора не изменится. Единственное исключение
 * busy: это мгновенная очередь, и второй заход через секунду часто проходит.
 */
export const odataRetry = (count: number, err: unknown) => {
  if (err instanceof Unauthorized) return false
  if (err instanceof OdataDown) return err.code === 'busy' && count < 1
  return count < 1
}

export function useOdata<T>(
  base: OdataBase,
  path: string | null,
  opts: { maxAge?: number; staleTime?: number } = {},
) {
  return useQuery({
    queryKey: ['1c', base, path, opts.maxAge ?? null],
    enabled: !!path,
    queryFn: () => odataGet<T>(base, path!, opts.maxAge),
    retry: odataRetry,
    // Ядро само кеширует ответ (для остатков 180 с). Свой кеш держим короче, чтобы
    // «обновить» на экране действительно шло в ядро, а не отдавало нашу же копию.
    staleTime: opts.staleTime ?? 30_000,
  })
}
